import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Minus, Plus, ShoppingBag, Check, Truck, Shield, RotateCcw, Star, MessageSquare } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarRating = ({ rating, onRate, interactive = false, size = 'md' }) => {
  const [hover, setHover] = useState(0);
  const sizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={!interactive}
          onClick={() => interactive && onRate(star)}
          onMouseEnter={() => interactive && setHover(star)}
          onMouseLeave={() => interactive && setHover(0)}
          className={interactive ? 'cursor-pointer' : 'cursor-default'}
        >
          <Star
            className={`${sizeClass} ${
              (interactive ? hover || rating : rating) >= star
                ? 'fill-[#C9A962] text-[#C9A962]'
                : 'text-[#D4D4D4]'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const ProductDetail = () => {
  const { productId } = useParams();
  const { addToCart, loading: cartLoading } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ total: 0, average: 0 });
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: '',
    author_name: '',
    author_email: ''
  });

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/products/${productId}`);
        setProduct(res.data);
        
        // Fetch related products
        const relatedRes = await axios.get(`${API}/products?category=${res.data.category}&limit=4`);
        setRelatedProducts(relatedRes.data.filter(p => p.id !== productId));
        
        // Fetch reviews
        const reviewsRes = await axios.get(`${API}/products/${productId}/reviews`);
        setReviews(reviewsRes.data.reviews);
        setReviewStats({
          total: reviewsRes.data.total_reviews,
          average: reviewsRes.data.average_rating
        });
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
    setQuantity(1);
    setAdded(false);
    setShowReviewForm(false);
    setReviewForm({ rating: 5, title: '', comment: '', author_name: '', author_email: '' });
    window.scrollTo(0, 0);
  }, [productId]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setSubmittingReview(true);
    
    try {
      await axios.post(`${API}/products/${productId}/reviews`, {
        ...reviewForm,
        product_id: productId
      });
      
      toast.success('Review submitted! It will be visible after moderation.');
      setShowReviewForm(false);
      setReviewForm({ rating: 5, title: '', comment: '', author_name: '', author_email: '' });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAddToCart = async () => {
    if (!product || product.stock <= 0) return;
    
    const result = await addToCart(product.id, quantity);
    if (result.success) {
      setAdded(true);
      toast.success(`Added ${quantity} item(s) to cart`);
      setTimeout(() => setAdded(false), 2000);
    } else {
      toast.error(result.error || 'Failed to add item');
    }
  };

  const incrementQuantity = () => {
    if (product && quantity < product.stock) {
      setQuantity(q => q + 1);
    }
  };

  const decrementQuantity = () => {
    if (quantity > 1) {
      setQuantity(q => q - 1);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-16">
        <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 animate-pulse">
            <div className="aspect-square bg-[#F5F5F5]"></div>
            <div className="space-y-4">
              <div className="h-8 bg-[#F5F5F5] w-3/4"></div>
              <div className="h-6 bg-[#F5F5F5] w-1/4"></div>
              <div className="h-24 bg-[#F5F5F5]"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-28 pb-16 flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-serif text-2xl font-semibold mb-4">Product not found</h1>
          <Link to="/store" className="text-[#737373] hover:text-[#0A0A0A]">
            ← Back to Store
          </Link>
        </div>
      </div>
    );
  }

  const isOutOfStock = product.stock <= 0;

  // Format price for display
  const formattedPrice = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: product.currency || 'USD'
  }).format(product.price);

  return (
    <div className="min-h-screen pt-28 pb-16" data-testid="product-detail-page">
      <SEO 
        title={product.name}
        description={product.description?.substring(0, 160) || `Buy ${product.name} - Premium quality from Branding Yard. ${formattedPrice}`}
        image={product.image_url}
        url={`/product/${product.id}`}
        type="product"
        product={{
          ...product,
          averageRating: reviewStats.average,
          reviewCount: reviewStats.total
        }}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Store', url: '/store' },
          { name: product.category, url: `/store?category=${product.category}` },
          { name: product.name }
        ]}
      />
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24">
        {/* Breadcrumb */}
        <Link 
          to="/store"
          className="inline-flex items-center text-sm text-[#737373] hover:text-[#0A0A0A] mb-8"
          data-testid="back-to-store"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back to Store
        </Link>

        {/* Product Content */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="aspect-square bg-[#F5F5F5] overflow-hidden">
            <img 
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
              loading="lazy"
              data-testid="product-image"
            />
          </div>

          {/* Details */}
          <div className="lg:py-8">
            <div className="sticky top-28">
              {/* Category */}
              <Link 
                to={`/store?category=${product.category}`}
                className="text-sm uppercase tracking-wider text-[#737373] hover:text-[#0A0A0A]"
              >
                {product.category.replace('-', ' ')}
              </Link>

              {/* Title */}
              <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#0A0A0A] mt-2" data-testid="product-name">
                {product.name}
              </h1>

              {/* Price */}
              <p className="text-2xl font-medium text-[#0A0A0A] mt-4" data-testid="product-price">
                ${product.price.toFixed(2)}
              </p>

              {/* Stock Status */}
              <div className="mt-4">
                {isOutOfStock ? (
                  <span className="inline-flex items-center text-sm text-red-600" data-testid="out-of-stock">
                    Out of Stock
                  </span>
                ) : product.stock <= 5 ? (
                  <span className="inline-flex items-center text-sm text-orange-600" data-testid="low-stock">
                    Only {product.stock} left in stock
                  </span>
                ) : (
                  <span className="inline-flex items-center text-sm text-green-600" data-testid="in-stock">
                    <Check className="w-4 h-4 mr-1" />
                    In Stock
                  </span>
                )}
              </div>

              {/* Description */}
              <p className="text-[#737373] leading-relaxed mt-6" data-testid="product-description">
                {product.description}
              </p>

              {/* Quantity & Add to Cart */}
              {!isOutOfStock && (
                <div className="mt-8 space-y-4">
                  {/* Quantity Selector */}
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-[#737373]">Quantity</span>
                    <div className="flex items-center border border-[#E5E5E5]">
                      <button
                        onClick={decrementQuantity}
                        disabled={quantity <= 1}
                        className="p-3 hover:bg-[#F5F5F5] disabled:opacity-50"
                        data-testid="decrease-quantity"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-16 text-center" data-testid="quantity-value">{quantity}</span>
                      <button
                        onClick={incrementQuantity}
                        disabled={quantity >= product.stock}
                        className="p-3 hover:bg-[#F5F5F5] disabled:opacity-50"
                        data-testid="increase-quantity"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Add to Cart Button */}
                  <Button
                    onClick={handleAddToCart}
                    disabled={cartLoading}
                    className={`w-full h-14 rounded-none btn-lift text-sm uppercase tracking-wide ${
                      added 
                        ? 'bg-green-600 hover:bg-green-600' 
                        : 'bg-[#0A0A0A] hover:bg-[#262626]'
                    } text-white`}
                    data-testid="add-to-cart-btn"
                  >
                    {added ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Added to Cart
                      </>
                    ) : (
                      <>
                        <ShoppingBag className="w-4 h-4 mr-2" />
                        Add to Cart
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Features */}
              <div className="mt-12 pt-8 border-t border-[#E5E5E5] space-y-4">
                <div className="flex items-center gap-4 text-sm text-[#737373]">
                  <Truck className="w-5 h-5" />
                  <span>Free shipping on orders over $200</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-[#737373]">
                  <Shield className="w-5 h-5" />
                  <span>Quality guaranteed products</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-[#737373]">
                  <RotateCcw className="w-5 h-5" />
                  <span>Easy returns within 30 days</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-24" data-testid="reviews-section">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-serif text-2xl md:text-3xl font-semibold text-[#0A0A0A]">
                Customer Reviews
              </h2>
              {reviewStats.total > 0 && (
                <div className="flex items-center gap-3 mt-2">
                  <StarRating rating={Math.round(reviewStats.average)} />
                  <span className="text-sm text-[#737373]">
                    {reviewStats.average.toFixed(1)} out of 5 ({reviewStats.total} review{reviewStats.total !== 1 ? 's' : ''})
                  </span>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              className="rounded-none gap-2"
              onClick={() => setShowReviewForm(!showReviewForm)}
            >
              <MessageSquare className="w-4 h-4" />
              Write a Review
            </Button>
          </div>

          {/* Review Form */}
          {showReviewForm && (
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-6 mb-8">
              <h3 className="font-semibold text-[#0A0A0A] mb-4">Write Your Review</h3>
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div>
                  <Label>Rating *</Label>
                  <div className="mt-1">
                    <StarRating
                      rating={reviewForm.rating}
                      onRate={(r) => setReviewForm({ ...reviewForm, rating: r })}
                      interactive
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Your Name *</Label>
                    <Input
                      value={reviewForm.author_name}
                      onChange={(e) => setReviewForm({ ...reviewForm, author_name: e.target.value })}
                      required
                      className="rounded-none mt-1"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={reviewForm.author_email}
                      onChange={(e) => setReviewForm({ ...reviewForm, author_email: e.target.value })}
                      required
                      className="rounded-none mt-1"
                      placeholder="john@example.com"
                    />
                    <p className="text-xs text-[#737373] mt-1">Your email won't be published</p>
                  </div>
                </div>

                <div>
                  <Label>Review Title</Label>
                  <Input
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                    className="rounded-none mt-1"
                    placeholder="Great product!"
                  />
                </div>

                <div>
                  <Label>Your Review *</Label>
                  <Textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    required
                    className="rounded-none mt-1 min-h-[100px]"
                    placeholder="Share your experience with this product..."
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={submittingReview}
                    className="rounded-none bg-[#0A0A0A] hover:bg-[#262626]"
                  >
                    {submittingReview ? 'Submitting...' : 'Submit Review'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-none"
                    onClick={() => setShowReviewForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Reviews List */}
          {reviews.length === 0 ? (
            <div className="text-center py-12 bg-[#FAFAFA] border border-[#E5E5E5]">
              <MessageSquare className="w-12 h-12 text-[#D4D4D4] mx-auto mb-4" />
              <p className="text-[#737373] mb-4">No reviews yet</p>
              <p className="text-sm text-[#737373]">Be the first to review this product!</p>
            </div>
          ) : (
            <div className="space-y-6">
              {reviews.map((review) => (
                <div key={review.id} className="border-b border-[#E5E5E5] pb-6 last:border-0">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <StarRating rating={review.rating} size="sm" />
                      {review.title && (
                        <h4 className="font-semibold text-[#0A0A0A] mt-1">{review.title}</h4>
                      )}
                    </div>
                    <span className="text-xs text-[#737373]">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-[#737373] text-sm mb-2">{review.comment}</p>
                  <p className="text-xs text-[#737373]">— {review.author_name}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-24" data-testid="related-products">
            <h2 className="font-serif text-2xl md:text-3xl font-semibold text-[#0A0A0A] mb-8">
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.slice(0, 4).map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
